import type { CreateOrderItemRequest, OrderItem, OrderItemVariant } from '../types/orders';
import type { Product, VariantGroup, ProductVariant } from '../types/products';
import type { DailyMenu } from '../types/dailyMenu';
import { newClientId } from '../utils/clientId';

/**
 * Precio de un renglón cuando no hay servidor que lo calcule.
 *
 * **Es una copia de la regla del backend** (`OrderService.AddVariantsToOrderItem` y
 * `CreateDailyMenuOrderItemAsync`), a propósito y con la misma aritmética: el ticket
 * que se imprime sin red tiene que cuadrar al centavo con lo que el servidor guarde
 * al sincronizar, o la cajera cobra un total y el corte muestra otro.
 *
 * Reglas:
 * - Producto normal: precio + la suma de los modificadores elegidos, por cantidad.
 * - Modificadores por cantidad (QuantitySplit, "2 con roja y 1 con verde"): cada parte
 *   paga el precio base más los modificadores comunes más el suyo.
 * - Menú del día: precio del menú + los modificadores de los tiempos elegidos.
 */
export interface OfflineCatalog {
  productsById: Map<string, Product>;
  variantsById: Map<string, ProductVariant>;
  dailyMenusById: Map<string, DailyMenu>;
}

/**
 * `allVariants` son todas las variantes del restaurante (`/products/variants`), también las
 * sueltas sin grupo que no vienen en `variantGroups`. Es opcional porque una caja que no ha
 * vuelto a calentar la carta desde esta versión todavía no la tiene guardada.
 */
export const buildOfflineCatalog = (
  products: Product[], variantGroups: VariantGroup[], dailyMenus: DailyMenu[],
  allVariants: ProductVariant[] = []
): OfflineCatalog => ({
  productsById: new Map(products.map((p) => [p.id, p])),
  variantsById: new Map([
    ...variantGroups.flatMap((g) => g.variants.map((v) => [v.id, v] as const)),
    ...allVariants.map((v) => [v.id, v] as const),
  ]),
  dailyMenusById: new Map(dailyMenus.map((m) => [m.id, m])),
});

export class OfflinePricingError extends Error {}

const priceRegularItem = (
  item: CreateOrderItemRequest, product: Product, catalog: OfflineCatalog
): { unitPrice: number; total: number; variants: OrderItemVariant[] } => {
  const selections = item.variantSelections
    ?? (item.variantIds ?? []).map((variantId) => ({ variantId, quantity: undefined }));

  // Una opción que no está en la copia se cobraría sin su modificador: el ticket saldría
  // por menos de lo que el servidor guarda al sincronizar. Mejor no dejar agregarla.
  const missing = selections.some((s) => !catalog.variantsById.has(s.variantId));
  if (missing) {
    throw new OfflinePricingError(
      `Una opción de ${product.name} no está guardada en la caja; no se puede agregar sin conexión.`);
  }

  // Igual que el servidor, que las busca entre las variantes DEL producto y se salta el resto.
  const chosen = selections
    .map((s) => ({ selection: s, variant: catalog.variantsById.get(s.variantId) }))
    .filter((x): x is { selection: typeof selections[number]; variant: ProductVariant } =>
      !!x.variant && x.variant.productId === product.id);

  const variants: OrderItemVariant[] = chosen.map(({ selection, variant }) => ({
    id: newClientId(),
    orderItemId: '',
    variantId: variant.id,
    variantName: variant.name,
    priceModifier: variant.priceModifier,
    quantity: selection.quantity,
  }));

  const split = chosen.filter((x) => x.selection.quantity != null);
  if (split.length > 0) {
    const commonModifiers = chosen
      .filter((x) => x.selection.quantity == null)
      .reduce((sum, x) => sum + x.variant.priceModifier, 0);
    const unitPrice = product.price + commonModifiers;
    const total = split.reduce(
      (sum, x) => sum + (unitPrice + x.variant.priceModifier) * (x.selection.quantity as number), 0);
    if (total > 0) return { unitPrice, total, variants };
  }

  const unitPrice = product.price + chosen.reduce((sum, x) => sum + x.variant.priceModifier, 0);
  return { unitPrice, total: unitPrice * item.quantity, variants };
};

/**
 * Renglón listo para pintar y para imprimir. Lanza <see cref="OfflinePricingError"/> si
 * el producto o el menú no están en la copia local: sin precio no se puede cobrar, y es
 * mejor decirlo al agregarlo que al momento de cobrar.
 */
export function priceOfflineItem(
  item: CreateOrderItemRequest, orderId: string, catalog: OfflineCatalog
): OrderItem {
  const product = catalog.productsById.get(item.productId);
  if (!product) {
    throw new OfflinePricingError('Este producto no está guardado en la caja; no se puede agregar sin conexión.');
  }
  if (!product.isActive) {
    throw new OfflinePricingError(`${product.name} ya no está en la carta; no se puede agregar sin conexión.`);
  }

  const id = item.id ?? newClientId();

  if (item.dailyMenuId) {
    const menu = catalog.dailyMenusById.get(item.dailyMenuId);
    if (!menu) {
      throw new OfflinePricingError('El menú del día no está guardado en la caja; no se puede agregar sin conexión.');
    }

    const optionsById = new Map(menu.courses.flatMap((c) => c.options.map((o) => [o.id, { course: c, option: o }] as const)));
    const selections = (item.dailyMenuSelections ?? []).map((sel) => {
      const found = sel.optionId ? optionsById.get(sel.optionId) : undefined;
      const course = found?.course ?? menu.courses.find((c) => c.id === sel.courseId);
      return {
        id: newClientId(),
        dailyMenuCourseId: sel.courseId,
        courseName: course?.name ?? '',
        dailyMenuOptionId: sel.optionId,
        optionName: found?.option.name ?? course?.fixedDescription ?? '',
        priceModifier: found?.option.priceModifier ?? 0,
        courseSortOrder: course?.sortOrder ?? 0,
        isSelectable: course?.isSelectable ?? false,
      };
    });

    const unitPrice = menu.price + selections.reduce((sum, s) => sum + s.priceModifier, 0);
    return {
      id,
      orderId,
      productId: item.productId,
      productName: menu.name,
      quantity: item.quantity,
      unitPrice,
      total: unitPrice * item.quantity,
      notes: item.notes,
      seatNumber: item.seatNumber,
      variants: [],
      isCancelled: false,
      dailyMenuId: menu.id,
      dailyMenuName: menu.name,
      dailyMenuSelections: selections,
      dailyMenuSelectableCourseCount: menu.courses.filter((c) => c.isSelectable).length,
    };
  }

  // Agotado: el servidor lo rechaza al sincronizar ("One or more products are not
  // available") y con él se cae toda la comanda y el cobro de esa mesa. Mejor decirlo al
  // agregarlo. (El producto ancla de un menú del día no pasa por aquí: el servidor tampoco
  // le pide disponibilidad.)
  if (!product.isAvailable) {
    throw new OfflinePricingError(`${product.name} está agotado; no se puede agregar sin conexión.`);
  }

  const { unitPrice, total, variants } = priceRegularItem(item, product, catalog);
  return {
    id,
    orderId,
    productId: item.productId,
    productName: product.name,
    quantity: item.quantity,
    unitPrice,
    total,
    notes: item.notes,
    seatNumber: item.seatNumber,
    variants: variants.map((v) => ({ ...v, orderItemId: id })),
    isCancelled: false,
  };
}

/** Total de la orden: la suma de sus renglones vivos, igual que en el backend. */
export const sumOfflineItems = (items: OrderItem[]): number =>
  items.filter((i) => !i.isCancelled).reduce((sum, i) => sum + i.total, 0);
